import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { AnalyticsEvents } from '../utils/analytics';
import { seedHistoricAnalytics } from '../utils/seedAnalytics';
import { useAuth } from '../contexts/AuthContext';
import { useSecurity } from '../contexts/SecurityContext';
import Header from '../components/Header';
import './AnalyticsPage.css';

const AnalyticsPage = () => {
  const { currentUser } = useAuth();
  const { banUser } = useSecurity();
  const ADMIN_UID = "qo7x9wiqlJThtRxqaKJ2AHst6Uf2";
  
  const [activeTab, setActiveTab] = useState('acquisition');
  const [telemetry, setTelemetry] = useState([]);
  const [bannedIds, setBannedIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedStatus, setSeedStatus] = useState('');
  const [autoShieldEnabled, setAutoShieldEnabled] = useState(localStorage.getItem('listen2_autoshield') === 'true');
  const [expandedUserId, setExpandedUserId] = useState(null);

  // --- Persistence ---
  useEffect(() => {
    localStorage.setItem('listen2_autoshield', autoShieldEnabled);
  }, [autoShieldEnabled]);

  // --- Ban Logic ---
  const handleBanAction = async (user, silent = false) => {
    const reason = user.isBotUA ? "Automated bot activity detected" : 
                   user.isFarming ? "Content manipulation / Stream farming detected" : 
                   "Suspicious behavioral burst detected";
    
    if (silent || window.confirm(`SECURITY ACTION: Permanently restrict access for ID: ${user.id}?\n\nReason: ${reason}\n\nThis will block the device and account immediately.`)) {
      const success = await banUser(user.id, reason);
      if (success && !silent) {
        alert(`SUCCESS: ID ${user.id} has been restricted and added to the blacklist.`);
      }
    }
  };

  // --- Data Aggregation Logic ---
  const metrics = useMemo(() => {
    const filteredTelemetry = telemetry.filter(t => 
      !bannedIds.has(t.userId) && !bannedIds.has(t.deviceId) && !bannedIds.has(t.id)
    );

    if (!filteredTelemetry.length) return null;

    const now = new Date();
    const last24h = filteredTelemetry.filter(t => (now - t.date) < 24 * 60 * 60 * 1000);
    const last7d = filteredTelemetry.filter(t => (now - t.date) < 7 * 24 * 60 * 60 * 1000);

    const signups = filteredTelemetry.filter(t => t.event === AnalyticsEvents.USER_SIGNUP);
    const sources = signups.reduce((acc, t) => {
      const source = t.source || 'Direct';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {});

    const plays = filteredTelemetry.filter(t => t.event === AnalyticsEvents.PLAY_START || t.event === 'analytics_play_start');
    const completions = filteredTelemetry.filter(t => t.event === AnalyticsEvents.PLAY_COMPLETE || t.event === 'analytics_play_complete');
    const completionRate = plays.length > 0 ? (completions.length / plays.length) * 100 : 0;

    const podcastStats = filteredTelemetry
      .filter(t => t.event === AnalyticsEvents.PLAY_START || t.event === AnalyticsEvents.PLAY_COMPLETE)
      .reduce((acc, t) => {
        const id = t.podcastId || t.id;
        const title = t.episodeTitle || t.title || 'Unknown Podcast';
        if (!acc[id]) {
          acc[id] = { title, plays: 0, completions: 0, uniqueListeners: new Set(), totalDuration: 0, playCountWithDuration: 0 };
        }
        if (t.event === AnalyticsEvents.PLAY_START) {
          acc[id].plays++;
          acc[id].uniqueListeners.add(t.userId || t.id);
          if (t.duration) {
            acc[id].totalDuration += t.duration;
            acc[id].playCountWithDuration++;
          }
        }
        if (t.event === AnalyticsEvents.PLAY_COMPLETE) acc[id].completions++;
        return acc;
      }, {});

    const topPodcasts = Object.values(podcastStats).sort((a, b) => b.plays - a.plays).slice(0, 10);

    const activeUsersByDay = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      activeUsersByDay[d.toLocaleDateString()] = new Set();
    }

    last7d.forEach(t => {
      const day = t.date.toLocaleDateString();
      if (activeUsersByDay[day]) activeUsersByDay[day].add(t.userId || t.id);
    });

    const dauTrend = Object.entries(activeUsersByDay).map(([day, users]) => ({ day, count: users.size })).reverse();
    const uniqueUsers = new Set(filteredTelemetry.map(t => t.userId || t.id));
    const returningUsers = filteredTelemetry.filter(t => t.userId && filteredTelemetry.some(p => p.userId === t.userId && p.id !== t.id && p.date < t.date));
    const retentionRate = uniqueUsers.size > 0 ? (returningUsers.length / uniqueUsers.size) * 100 : 0;

    const userGroups = telemetry.reduce((acc, t) => {
      const uid = t.userId || t.id;
      if (!acc[uid]) acc[uid] = { id: uid, events: [], ua: t.userAgent || '' };
      acc[uid].events.push(t);
      return acc;
    }, {});

    const suspiciousUsers = Object.values(userGroups).map(u => {
      const sortedEvents = [...u.events].sort((a, b) => a.date - b.date);
      let maxBurst = 0;
      for (let i = 0; i < sortedEvents.length; i++) {
        const start = sortedEvents[i].date;
        const burst = sortedEvents.filter(e => (e.date - start) >= 0 && (e.date - start) <= 60000).length;
        if (burst > maxBurst) maxBurst = burst;
      }
      const isBotUA = /bot|crawl|spider|headless|selenium|puppeteer/i.test(u.ua);
      const playsPerPodcast = u.events.filter(e => e.event === AnalyticsEvents.PLAY_START).reduce((acc, e) => {
        acc[e.podcastId || 'unknown'] = (acc[e.podcastId || 'unknown'] || 0) + 1;
        return acc;
      }, {});
      const maxPlaysOnOnePodcast = Math.max(0, ...Object.values(playsPerPodcast));
      const isFarming = maxPlaysOnOnePodcast > 10;

      // Calculate "Abnormal Persistence" (>6hrs at a stretch)
      let isExtremePersistent = false;
      let currentSessionStart = sortedEvents[0]?.date;
      let lastEventTime = sortedEvents[0]?.date;
      
      for (let i = 1; i < sortedEvents.length; i++) {
        const eventTime = sortedEvents[i].date;
        const gap = eventTime - lastEventTime;
        
        if (gap > 30 * 60 * 1000) { // 30 min gap starts a "new session"
          const sessionDuration = lastEventTime - currentSessionStart;
          if (sessionDuration > 6 * 60 * 60 * 1000) isExtremePersistent = true;
          currentSessionStart = eventTime;
        }
        lastEventTime = eventTime;
      }
      // Check final session
      if (lastEventTime - currentSessionStart > 6 * 60 * 60 * 1000) isExtremePersistent = true;

      let riskScore = 0;
      if (maxBurst > 20) riskScore += 50;
      if (maxBurst > 50) riskScore += 30;
      if (isBotUA) riskScore += 100;
      if (isFarming) riskScore += 70;
      if (isExtremePersistent) riskScore += 80;

      return { 
        ...u, 
        maxBurst, 
        isBotUA, 
        isFarming, 
        isExtremePersistent,
        maxPlaysOnOnePodcast, 
        riskScore, 
        lastEvent: sortedEvents[sortedEvents.length - 1].date 
      };
    }).filter(u => u.riskScore > 0).sort((a, b) => b.riskScore - a.riskScore);

    const totalBots = suspiciousUsers.filter(u => u.isBotUA).length;
    const totalBurstUsers = suspiciousUsers.filter(u => u.maxBurst > 20).length;
    const totalFarmingUsers = suspiciousUsers.filter(u => u.isFarming).length;
    const totalPersistentUsers = suspiciousUsers.filter(u => u.isExtremePersistent).length;

    const visitors = filteredTelemetry.filter(t => t.event === AnalyticsEvents.PAGE_VIEW || t.event === 'analytics_page_view').length;
    const firstPlays = plays.length;
    const follows = filteredTelemetry.filter(t => t.event === AnalyticsEvents.PODCAST_FOLLOW || t.event === 'analytics_podcast_follow').length;
    const signupEvents = filteredTelemetry.filter(t => t.event === AnalyticsEvents.USER_SIGNUP || t.event === 'analytics_user_signup').length;

    return {
      acquisition: { totalSignups: signups.length, sources, signupsLast24h: last24h.filter(t => t.event === AnalyticsEvents.USER_SIGNUP).length },
      engagement: { totalPlays: plays.length, completionRate },
      content: { topPodcasts },
      retention: { retentionRate, dauTrend, uniqueUsers: uniqueUsers.size },
      security: { suspiciousUsers, totalBots, totalBurstUsers, totalFarmingUsers, totalPersistentUsers },
      audit: telemetry.slice(0, 200), // Show latest 200 events in audit log
      funnel: [
        { label: 'Visitors', value: visitors, desc: 'Users who opened the app' },
        { label: 'Signups', value: signupEvents, desc: 'Users who signed up' },
        { label: 'First Listen', value: firstPlays, desc: 'Users who started a podcast' },
        { label: 'Followers', value: follows, desc: 'Users who followed a podcast' }
      ]
    };
  }, [telemetry, bannedIds]);

  // --- Effects ---
  useEffect(() => {
    if (currentUser?.uid === ADMIN_UID) {
      fetchTelemetry();
    }
  }, [currentUser]);

  useEffect(() => {
    if (autoShieldEnabled && metrics?.security?.suspiciousUsers) {
      const highRiskUsers = metrics.security.suspiciousUsers.filter(u => u.riskScore >= 100 && !bannedIds.has(u.id));
      if (highRiskUsers.length > 0) {
        highRiskUsers.forEach(u => handleBanAction(u, true));
      }
    }
  }, [autoShieldEnabled, metrics, bannedIds]);

  const fetchTelemetry = async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, 'telemetry'), orderBy('timestamp', 'desc'), limit(2000));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => {
        const raw = doc.data();
        let date = new Date();
        try {
          if (raw.timestamp?.toDate) date = raw.timestamp.toDate();
          else if (raw.timestamp instanceof Date) date = raw.timestamp;
        } catch (e) {}
        return { id: doc.id, ...raw, date };
      });
      setTelemetry(data);

      const bannedSnapshot = await getDocs(collection(db, 'banned_users'));
      setBannedIds(new Set(bannedSnapshot.docs.map(doc => doc.id)));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeed = async () => {
    if (window.confirm("Seed 30 days of historic data?")) {
      setIsSeeding(true);
      setSeedStatus('Initializing...');
      await seedHistoricAnalytics((status) => setSeedStatus(status));
      await fetchTelemetry();
      setIsSeeding(false);
      setSeedStatus('');
    }
  };

  if (currentUser?.uid !== ADMIN_UID) {
    return <div className="admin-login-overlay"><h2>Access Denied</h2></div>;
  }

  return (
    <>
      <Header />
      <div className="analytics-container">
        <div className="analytics-header">
          <h1>Site Analytics</h1>
          <button className="btn-seed" onClick={handleSeed} disabled={isSeeding}>
            {isSeeding ? `Seeding (${seedStatus})...` : 'Seed Historic Data'}
          </button>
        </div>

        <nav className="analytics-tabs">
          {['acquisition', 'engagement', 'content', 'retention', 'funnel', 'security', 'audit'].map(tab => (
            <button 
              key={tab} 
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`} 
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>

        {isLoading ? <p>Loading...</p> : !metrics ? <p>No data available.</p> : (
          <div className="analytics-content">
            {activeTab === 'acquisition' && (
              <div className="tab-acquisition">
                <div className="analytics-grid">
                  <div className="stat-card">
                    <div className="stat-label">Total Signups</div>
                    <div className="stat-value">{metrics.acquisition.totalSignups}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">New Users (24h)</div>
                    <div className="stat-value">{metrics.acquisition.signupsLast24h}</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'engagement' && (
              <div className="tab-engagement">
                <div className="analytics-grid">
                  <div className="stat-card">
                    <div className="stat-label">Total Plays</div>
                    <div className="stat-value">{metrics.engagement.totalPlays}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Completion Rate</div>
                    <div className="stat-value">{metrics.engagement.completionRate.toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div className="tab-content">
                <table className="analytics-table">
                  <thead>
                    <tr><th>Podcast</th><th>Plays</th><th>Retention</th></tr>
                  </thead>
                  <tbody>
                    {metrics.content.topPodcasts.map((p, i) => (
                      <tr key={i}>
                        <td>{p.title}</td>
                        <td>{p.plays}</td>
                        <td>{((p.completions/p.plays)*100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'funnel' && (
              <div className="tab-funnel">
                <div className="funnel-container">
                  {metrics.funnel.map(stage => (
                    <div key={stage.label} className="funnel-stage" style={{ width: '80%' }}>
                      {stage.label}: {stage.value}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="tab-security">
                <div className="analytics-grid">
                  <div className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
                    <div className="stat-label">Flagged Bots</div>
                    <div className="stat-value">{metrics.security.totalBots}</div>
                  </div>
                  <div className="stat-card" style={{ borderLeft: '4px solid #1a73e8' }}>
                    <div className="stat-label">Auto-Shield Mode</div>
                    <div className="stat-value">
                      <button className={`btn-small ${autoShieldEnabled ? 'active' : ''}`} onClick={() => setAutoShieldEnabled(!autoShieldEnabled)}>
                        {autoShieldEnabled ? 'ACTIVE' : 'OFF'}
                      </button>
                    </div>
                  </div>
                  <div className="stat-card" style={{ borderLeft: '4px solid #06b6d4' }}>
                    <div className="stat-label">Extreme Persistence</div>
                    <div className="stat-value">{metrics.security.totalPersistentUsers}</div>
                    <div className="stat-desc">Active for >6 hours straight.</div>
                  </div>
                </div>

                <table className="analytics-table">
                  <thead>
                    <tr><th>User</th><th>Risk</th><th>Signature</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {metrics.security.suspiciousUsers.map((u, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: '10px' }}>{u.id}</td>
                        <td>{u.riskScore >= 100 ? 'HIGH' : 'MEDIUM'}</td>
                        <td style={{ fontSize: '10px' }}>
                          {u.isBotUA ? '🤖 Bot' : u.isFarming ? '🚜 Stream Farm' : u.isExtremePersistent ? '⏳ Persistent' : '⚡ Burst'}
                        </td>
                        <td>
                          {bannedIds.has(u.id) ? 'BANNED' : <button className="btn-small-danger" onClick={() => handleBanAction(u)}>Ban</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeTab === 'audit' && (
              <div className="tab-audit">
                <div className="chart-container">
                  <div className="chart-header">
                    <h3>Global Audit Log</h3>
                    <p className="chart-subtitle">Real-time sequence of all platform activity, including guest and authenticated sessions.</p>
                  </div>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>User ID / Fingerprint</th>
                        <th>Event</th>
                        <th>Metadata</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.audit.map((log, i) => (
                        <tr key={i} style={{ opacity: bannedIds.has(log.userId || log.deviceId) ? 0.6 : 1 }}>
                          <td style={{ fontSize: '11px', color: '#1a73e8' }}>{log.date.toLocaleTimeString()}</td>
                          <td style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                            {log.userId || log.deviceId || 'anon'}
                          </td>
                          <td>
                            <span className="risk-badge" style={{ background: '#f1f3f4', color: '#202124', border: '1px solid #ddd' }}>
                              {log.event.replace('analytics_', '').toUpperCase()}
                            </span>
                          </td>
                          <td style={{ fontSize: '11px' }}>
                            {log.episodeTitle || log.path || log.query || '-'}
                          </td>
                          <td>
                            {bannedIds.has(log.userId || log.deviceId) ? (
                              <span style={{ color: '#ef4444', fontSize: '10px', fontWeight: 600 }}>BLOCKED</span>
                            ) : (
                              <span style={{ color: '#34a853', fontSize: '10px' }}>LIVE</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AnalyticsPage;
